// uiarc: calendar (https://uiarc.dev/r/calendar.json) — CSS Modules yerine Tailwind; react-day-picker yerine uiarc'ın
// kendi ızgarası. Aylar tek şeritte yan yana durur, ay değişince şerit yayla kayar; seçili gün tek bir disktir ve
// günden güne süzülür (haritalanmış konumla, ölçmeden). Başlık ayı yönüne göre kayarak değişir.
// Sapmalar: gün başı Pazartesi (`weekStartsOn`), metinler `messages.calendar`, "bugün" tesis takvim günü
// (`lib/use-today.ts`), vurgu = marka yeşili (`control-on`). Tarihler yerel gece yarısı `Date`; Cube metinleri
// `new Date(...)` ile buraya girmez (`lib/calendar-date.ts`).
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  animate,
  AnimatePresence,
  motion,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useTransform,
  type AnimationPlaybackControls,
  type MotionValue,
  type Variants,
} from 'motion/react'
import {
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import {
  addDays,
  addMonths,
  dateKey,
  monthStart,
  sameDay,
  sameMonth,
  shiftMonths,
  startOfDay,
} from '@/lib/calendar-date'
import { messages } from '@/lib/messages'
import { motionTokens } from '@/lib/motion'
import { useToday } from '@/lib/use-today'
import { cn } from '@/lib/utils'

export type CalendarDateMatcher = (date: Date) => boolean

export interface CalendarProps {
  value?: Date
  onChange?: (date: Date) => void
  month?: Date
  onMonthChange?: (month: Date) => void
  minDate?: Date
  maxDate?: Date
  disabledDates?: CalendarDateMatcher
  locale?: string
  /** 0 Pazar, 1 Pazartesi. */
  weekStartsOn?: 0 | 1
  className?: string
  /** Bugüne dönen ve uygunsa bugünü seçen düğme ekler. */
  showToday?: boolean
}

const isBefore = (a: Date, b?: Date) =>
  Boolean(b && startOfDay(a).getTime() < startOfDay(b).getTime())
const isAfter = (a: Date, b?: Date) =>
  Boolean(b && startOfDay(a).getTime() > startOfDay(b).getTime())

const { spring, duration, ease } = motionTokens

/** Aylar tek şeritte durur. Hızlı tıklar aynı yayı yeniden hedefler; ne kuyruk ne de üst üste panel olur. */
const stripSpring = { type: 'spring', visualDuration: 0.36, bounce: 0, restDelta: 0.002 } as const
const monthIndex = (date: Date) => date.getFullYear() * 12 + date.getMonth()
const fromIndex = (index: number) => new Date(Math.floor(index / 12), ((index % 12) + 12) % 12, 1)

/** Başlık yerinde çapraz geçer: eski ay şeridin gittiği tarafa çıkar, yenisi öbür taraftan gelir. */
const titleSlide: Variants = {
  enter: (direction: number) => ({ opacity: 0, x: direction * 12 }),
  center: { opacity: 1, x: 0, transition: { duration: duration.standard, ease: ease.enter } },
  exit: (direction: number) => ({
    opacity: 0,
    x: direction * -12,
    transition: { duration: 0.14, ease: ease.standard },
  }),
}
/** Azaltılmış harekette başlık boşluk çerçevesi olmadan yerinde değişir. */
const titleFade: Variants = {
  enter: { opacity: 1, x: 0 },
  center: { opacity: 1, x: 0, transition: { duration: 0 } },
  exit: { opacity: 0, transition: { duration: 0 } },
}

/**
 * Bir ayın seçili diski. Hafta ve gün sırasına göre konumlanır (ölçülmez); herhangi iki gün arasında düz çizgide
 * süzülür, şerit kayarken de süzülmeye devam eder, yeni seçimde yarı yolda hedef değiştirir. Yalnız seçim bu aya
 * girip çıkarken büyür ya da solar.
 */
function SelectionDisc({ cell, reduced }: { cell: number; reduced: boolean }) {
  const visible = cell >= 0
  const col = useMotionValue(visible ? cell % 7 : 0)
  const row = useMotionValue(visible ? Math.floor(cell / 7) : 0)
  const opacity = useMotionValue(visible ? 1 : 0)
  const scale = useMotionValue(visible ? 1 : 0.6)
  // Her adım bir hücre artı 4px boşluk; yüzdeler diskin kendi boyutuna (= hücre) göre çözülür.
  const transform = useTransform(
    () =>
      `translate(calc(${col.get()} * (100% + 4px)), calc(${row.get()} * (100% + 4px))) scale(${scale.get()})`,
  )
  const hidden = useRef(!visible)
  useLayoutEffect(() => {
    if (!visible) {
      hidden.current = true
      const fade = reduced ? { duration: 0 } : { duration: 0.14, ease: ease.standard }
      const controls = [animate(opacity, 0, fade), animate(scale, 0.6, fade)]
      return () => controls.forEach((control) => control.stop())
    }
    const nextCol = cell % 7
    const nextRow = Math.floor(cell / 7)
    const controls: AnimationPlaybackControls[] = []
    if (hidden.current || reduced) {
      col.jump(nextCol)
      row.jump(nextRow)
    } else controls.push(animate(col, nextCol, spring.morph), animate(row, nextRow, spring.morph))
    hidden.current = false
    controls.push(
      animate(
        opacity,
        1,
        reduced ? { duration: 0 } : { duration: duration.fast, ease: ease.enter },
      ),
      animate(scale, 1, reduced ? { duration: 0 } : spring.snappy),
    )
    return () => controls.forEach((control) => control.stop())
  }, [cell, visible, reduced, col, row, opacity, scale])
  return (
    <motion.span
      aria-hidden="true"
      className="pointer-events-none absolute top-0 left-0 aspect-square w-[calc((100%-24px)/7)] will-change-transform"
      style={{ transform, opacity }}
    >
      <span
        className={cn(
          'bg-control-on absolute inset-0 rounded-full',
          '[transition:background-color_var(--duration-fast)_var(--ease-standard),scale_var(--duration-spring)_var(--ease-spring)]',
          'group-has-[button[data-selected=true]:hover]/pane:bg-[color-mix(in_oklab,var(--control-on)_86%,black)]',
          'group-has-[button[data-selected=true]:active:not(:disabled)]/pane:scale-[0.94]',
          'group-has-[button[data-selected=true]:active:not(:disabled)]/pane:[transition:background-color_var(--duration-fast)_var(--ease-standard),scale_100ms_var(--ease-standard)]',
          'motion-reduce:transition-none',
        )}
      />
    </motion.span>
  )
}

/** Şeritteki tek ay. Yalnız gidilen ay odaklanabilir ve okunur; yanından kayan ay etkisizdir. */
function MonthPane({
  index,
  position,
  present,
  children,
}: {
  index: number
  position: MotionValue<number>
  present: boolean
  children: ReactNode
}) {
  const x = useTransform(
    position,
    (value) => `calc(${(index - value) * 100}% + ${(index - value) * 16}px)`,
  )
  const opacity = useTransform(position, (value) => 1 - Math.min(1, Math.abs(index - value)) * 0.6)
  return (
    <motion.div
      className="group/pane relative col-start-1 row-start-1 min-w-0 will-change-transform data-[present]:z-[1] [&[inert]]:pointer-events-none"
      style={{ x, opacity }}
      data-present={present || undefined}
      aria-hidden={present ? undefined : true}
      inert={!present}
    >
      {children}
    </motion.div>
  )
}

const headerButton = cn(
  'text-text-secondary grid h-8 cursor-pointer place-items-center rounded-pill border-0 bg-transparent [-webkit-tap-highlight-color:transparent]',
  '[transition:background-color_var(--duration-fast)_var(--ease-standard),color_var(--duration-fast)_var(--ease-standard),opacity_var(--duration-fast)_var(--ease-standard),scale_var(--duration-spring)_var(--ease-spring)]',
  'not-aria-disabled:hover:bg-surface-muted not-aria-disabled:hover:text-foreground',
  'not-aria-disabled:active:scale-[0.96] not-aria-disabled:active:[transition:background-color_var(--duration-fast)_var(--ease-standard),color_var(--duration-fast)_var(--ease-standard),opacity_var(--duration-fast)_var(--ease-standard),scale_100ms_var(--ease-standard)]',
  'aria-disabled:cursor-default aria-disabled:opacity-35 motion-reduce:transition-none motion-reduce:active:scale-100',
)

function Calendar({
  value,
  onChange,
  month: controlledMonth,
  onMonthChange,
  minDate,
  maxDate,
  disabledDates,
  locale = 'tr-TR',
  weekStartsOn = 1,
  className,
  showToday = false,
}: CalendarProps) {
  const titleId = useId()
  const today = useToday()
  const reducedMotion = useReducedMotion() ?? false
  const [internalMonth, setInternalMonth] = useState(() => (value ? monthStart(value) : undefined))
  const [focusedDate, setFocusedDate] = useState(value)
  const viewportRef = useRef<HTMLDivElement>(null)
  const focusRequest = useRef<string | null>(null)
  // Değer ya da ay yoksa takvim bugünü bekler, sonra o aya bir kez yerleşir.
  const fallback = value ?? today
  const month = controlledMonth
    ? monthStart(controlledMonth)
    : (internalMonth ?? (fallback && monthStart(fallback)))
  if (!controlledMonth && !internalMonth && month) setInternalMonth(month)
  const monthTime = month?.getTime()
  const monthKey = month ? dateKey(month) : 'pending'
  // Yön ayın kendisinden okunur: oklar, tuşlar, Bugün ve denetlenen ay aynı yöne gider.
  const [shownMonth, setShownMonth] = useState(monthTime)
  const [direction, setDirection] = useState(0)
  if (shownMonth !== monthTime) {
    setShownMonth(monthTime)
    setDirection(
      shownMonth === undefined || monthTime === undefined ? 0 : monthTime > shownMonth ? 1 : -1,
    )
  }
  const weeks = useMemo(
    () => (monthTime === undefined ? [] : makeWeeks(new Date(monthTime), weekStartsOn)),
    [monthTime, weekStartsOn],
  )
  // Şerit konumu ay cinsindendir. Konumun iki yanındaki paneller artı gidilen ay çizilir.
  const target = month ? monthIndex(month) : 0
  const position = useMotionValue(target)
  const [span, setSpan] = useState<[number, number]>([target, target])
  useMotionValueEvent(position, 'change', (latest) => {
    const next: [number, number] = [Math.floor(latest + 1e-3), Math.ceil(latest - 1e-3)]
    setSpan((current) => (current[0] === next[0] && current[1] === next[1] ? current : next))
  })
  const ready = month !== undefined
  const placed = useRef(ready)
  useLayoutEffect(() => {
    if (!ready) return
    const from = position.get()
    if (from === target) return
    // İlk ay ve azaltılmış hareket yerinde iner; gerisi şeritte kayar.
    if (!placed.current || reducedMotion) {
      placed.current = true
      position.jump(target)
      return
    }
    // Uzun atlamalar (Bugün, Shift ile yıl) bir ay uzaktan başlar; arada kalan aylar taranmaz.
    if (Math.abs(target - from) > 2) position.jump(target - Math.sign(target - from))
    const controls = animate(position, target, stripSpring)
    return () => controls.stop()
  }, [ready, target, reducedMotion, position])
  const paneIndexes = Array.from(new Set([span[0], span[1], target])).sort((a, b) => a - b)
  const formatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }),
    [locale],
  )
  const weekdayFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { weekday: 'short' }),
    [locale],
  )
  const weekdays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, index) =>
        weekdayFormatter.format(new Date(2024, 0, 7 + weekStartsOn + index)),
      ),
    [weekdayFormatter, weekStartsOn],
  )
  const monthLabel = month ? formatter.format(month) : ''
  // Seçili disk günler arasında süzülür; her sayı diskin gerçekten üstünden geçtiği anda renk değiştirir:
  // kısa atlamada ayrılan gün, uzun atlamada varılan gün daha uzun bekler.
  const valueKey = value ? dateKey(value) : ''
  const [last, setLast] = useState({ key: valueKey, date: value })
  const [handoff, setHandoff] = useState({ from: '', to: '', distance: 0 })
  if (last.key !== valueKey) {
    const days =
      last.date && value
        ? Math.round((startOfDay(value).getTime() - startOfDay(last.date).getTime()) / 864e5)
        : 0
    const columns = last.date && value ? value.getDay() - last.date.getDay() : 0
    setLast({ key: valueKey, date: value })
    setHandoff({
      from: last.key,
      to: valueKey,
      distance: Math.hypot(columns, (days - columns) / 7),
    })
  }
  const numberDelay = (key: string): CSSProperties | undefined => {
    if (!handoff.distance || reducedMotion || (key !== handoff.to && key !== handoff.from))
      return undefined
    const delay =
      key === handoff.to
        ? Math.min(260, 95 + 85 * Math.log(handoff.distance))
        : Math.max(20, 130 / handoff.distance)
    return { '--number-delay': `${Math.round(delay)}ms` } as CSSProperties
  }

  const isDisabled = (date: Date) =>
    isBefore(date, minDate) || isAfter(date, maxDate) || Boolean(disabledDates?.(date))
  const previousMonth = month && addMonths(month, -1)
  const nextMonth = month && addMonths(month, 1)
  const previousDisabled = Boolean(
    previousMonth && minDate && previousMonth.getTime() < monthStart(minDate).getTime(),
  )
  const nextDisabled = Boolean(
    nextMonth && maxDate && nextMonth.getTime() > monthStart(maxDate).getTime(),
  )
  const todaySelectable = Boolean(today && !isDisabled(today) && onChange)
  const todayIdle = Boolean(
    today && sameMonth(month, today) && (!todaySelectable || sameDay(value, today)),
  )
  // Gezinen sekme durağı: son odaklanan gün, yoksa seçili, yoksa bugün, yoksa ayın ilk açık günü.
  const tabbableKey = (() => {
    const open = (date?: Date) =>
      date && sameMonth(date, month) && !isDisabled(date) ? dateKey(date) : ''
    return (
      open(focusedDate) ||
      open(value) ||
      open(today) ||
      dateKey(
        weeks.flat().find((date) => sameMonth(date, month) && !isDisabled(date)) ?? new Date(0),
      )
    )
  })()

  const changeMonth = (next: Date) => {
    const normalized = monthStart(next)
    if (!controlledMonth) setInternalMonth(normalized)
    onMonthChange?.(normalized)
  }

  /** Klavye odağı, şerit kaymaya başladığı karede yeni güne iner. */
  useLayoutEffect(() => {
    const key = focusRequest.current
    if (!key) return
    const button = viewportRef.current?.querySelector<HTMLButtonElement>(
      `[data-present] [data-date="${key}"]`,
    )
    if (button) {
      focusRequest.current = null
      button.focus({ preventScroll: true })
    }
  })

  /** Klavye hareketini izinli aralığa sıkıştırır, engelli günleri gidiş yönünde atlar. */
  const moveFocus = (from: Date, to: Date, step: number) => {
    let next =
      isBefore(to, minDate) && minDate
        ? startOfDay(minDate)
        : isAfter(to, maxDate) && maxDate
          ? startOfDay(maxDate)
          : to
    for (let tries = 0; tries < 42 && isDisabled(next); tries += 1) next = addDays(next, step)
    if (isDisabled(next) || sameDay(next, from)) return
    setFocusedDate(next)
    focusRequest.current = dateKey(next)
    if (!sameMonth(next, month)) changeMonth(next)
  }

  const onDayKeyDown = (event: KeyboardEvent<HTMLButtonElement>, date: Date) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (!isDisabled(date)) onChange?.(date)
      return
    }
    const weekday = (date.getDay() - weekStartsOn + 7) % 7
    const moves: Record<string, [Date, number]> = {
      ArrowLeft: [addDays(date, -1), -1],
      ArrowRight: [addDays(date, 1), 1],
      ArrowUp: [addDays(date, -7), -1],
      ArrowDown: [addDays(date, 7), 1],
      Home: [addDays(date, -weekday), 1],
      End: [addDays(date, 6 - weekday), -1],
      PageUp: [shiftMonths(date, event.shiftKey ? -12 : -1), -1],
      PageDown: [shiftMonths(date, event.shiftKey ? 12 : 1), 1],
    }
    const move = moves[event.key]
    if (!move) return
    event.preventDefault()
    moveFocus(date, move[0], move[1])
  }

  const goToToday = () => {
    if (!today || !month || todayIdle) return
    setFocusedDate(today)
    if (!sameMonth(today, month)) changeMonth(today)
    if (todaySelectable && !sameDay(value, today)) onChange?.(today)
  }

  // Her ay altı hafta gösterir; günün hücresi ızgaradaki ilk güne uzaklığıdır.
  const cellOf = (paneMonth: Date) => {
    if (!value) return -1
    const first = makeWeeks(paneMonth, weekStartsOn)[0]![0]!
    const index = Math.round((startOfDay(value).getTime() - first.getTime()) / 864e5)
    return index >= 0 && index < 42 ? index : -1
  }

  const renderMonth = (paneMonth: Date, present: boolean) => (
    <>
      <SelectionDisc cell={cellOf(paneMonth)} reduced={reducedMotion} />
      <div className="grid gap-y-1" role="grid" aria-label={formatter.format(paneMonth)}>
        {makeWeeks(paneMonth, weekStartsOn).map((week) => (
          <div key={dateKey(week[0]!)} className="grid grid-cols-7 gap-x-1" role="row">
            {week.map((date) => {
              const key = dateKey(date)
              const selected = sameDay(date, value)
              const isToday = sameDay(date, today)
              return (
                <button
                  key={key}
                  type="button"
                  role="gridcell"
                  data-date={key}
                  data-selected={selected}
                  data-today={isToday || undefined}
                  data-outside={!sameMonth(date, paneMonth) || undefined}
                  aria-label={date.toLocaleDateString(locale, { dateStyle: 'full' })}
                  aria-selected={selected}
                  aria-current={isToday ? 'date' : undefined}
                  tabIndex={present && key === tabbableKey ? 0 : -1}
                  disabled={isDisabled(date)}
                  style={present ? numberDelay(key) : undefined}
                  className={cn(
                    'group/day text-foreground relative grid aspect-square w-full min-w-0 cursor-pointer place-items-center rounded-full border-0 bg-transparent p-0 text-sm [line-height:1] font-normal tabular-nums [-webkit-tap-highlight-color:transparent]',
                    '[transition:color_var(--duration-fast)_var(--ease-standard)_var(--number-delay,0ms)]',
                    "before:absolute before:inset-0 before:rounded-[inherit] before:bg-transparent before:content-[''] before:[transition:background-color_var(--duration-fast)_var(--ease-standard),scale_var(--duration-spring)_var(--ease-spring)]",
                    'hover:not-disabled:not-data-[selected=true]:before:bg-surface-muted',
                    'active:not-disabled:before:scale-[0.94] active:not-disabled:before:[transition:background-color_var(--duration-fast)_var(--ease-standard),scale_100ms_var(--ease-standard)]',
                    'data-[selected=true]:text-control-glyph data-[selected=true]:font-medium data-[today]:font-medium',
                    'data-[outside]:text-text-muted data-[selected=true]:data-[outside]:text-control-glyph',
                    'disabled:text-text-muted disabled:cursor-default',
                    'motion-reduce:transition-none motion-reduce:before:transition-none',
                  )}
                  onFocus={() => setFocusedDate(date)}
                  onKeyDown={(event) => onDayKeyDown(event, date)}
                  onClick={() => onChange?.(date)}
                >
                  <span
                    className={cn(
                      'relative',
                      '[transition:opacity_var(--duration-fast)_var(--ease-standard)_var(--number-delay,0ms),scale_var(--duration-spring)_var(--ease-spring)]',
                      'group-active/day:group-not-disabled/day:scale-[0.94]',
                      'group-data-[outside]/day:not-group-data-[selected=true]/day:opacity-80',
                      'group-disabled/day:opacity-40 group-disabled/day:group-data-[outside]/day:opacity-30',
                      "group-data-[today]/day:after:absolute group-data-[today]/day:after:top-[calc(100%+4px)] group-data-[today]/day:after:left-1/2 group-data-[today]/day:after:-ml-0.5 group-data-[today]/day:after:size-1 group-data-[today]/day:after:rounded-full group-data-[today]/day:after:bg-current group-data-[today]/day:after:content-['']",
                      'motion-reduce:transition-none',
                    )}
                  >
                    {date.getDate()}
                  </span>
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </>
  )

  return (
    <section
      data-slot="calendar"
      className={cn('text-foreground w-[min(100%,328px)] min-w-0', className)}
      aria-labelledby={titleId}
    >
      <div className="@container mb-3.5 grid min-h-8.5 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1">
        <h2
          id={titleId}
          className="m-0 min-w-0 text-base font-medium [letter-spacing:inherit] @max-[259px]:col-span-full"
        >
          <span className="sr-only">{monthLabel}</span>
          <span className="leading-body grid [line-height:var(--leading-body)]" aria-hidden="true">
            <AnimatePresence initial={false} custom={direction}>
              {month && (
                <motion.span
                  key={monthKey}
                  className="col-start-1 row-start-1 inline-block justify-self-start whitespace-nowrap tabular-nums"
                  custom={direction}
                  variants={reducedMotion || !direction ? titleFade : titleSlide}
                  initial="enter"
                  animate="center"
                  exit="exit"
                >
                  {monthLabel}
                </motion.span>
              )}
            </AnimatePresence>
          </span>
        </h2>
        <span className="sr-only" aria-live="polite">
          {direction ? monthLabel : ''}
        </span>
        <div className="-mr-1.5 inline-flex flex-none items-center gap-0.5 @max-[259px]:col-span-full @max-[259px]:justify-self-end">
          {showToday && (
            <button
              type="button"
              className={cn(headerButton, 'px-2.75 text-sm leading-none font-medium')}
              aria-disabled={todayIdle || undefined}
              aria-label={
                today
                  ? `${messages.calendar.today}, ${today.toLocaleDateString(locale, { dateStyle: 'full' })}`
                  : messages.calendar.today
              }
              onClick={goToToday}
            >
              {messages.calendar.today}
            </button>
          )}
          <button
            type="button"
            className={cn(headerButton, 'w-8')}
            aria-label={messages.calendar.previousMonth}
            aria-disabled={previousDisabled || undefined}
            onClick={() => {
              if (!previousDisabled && previousMonth) changeMonth(previousMonth)
            }}
          >
            <ChevronLeft className="size-4" strokeWidth={1.75} aria-hidden="true" />
          </button>
          <button
            type="button"
            className={cn(headerButton, 'w-8')}
            aria-label={messages.calendar.nextMonth}
            aria-disabled={nextDisabled || undefined}
            onClick={() => {
              if (!nextDisabled && nextMonth) changeMonth(nextMonth)
            }}
          >
            <ChevronRight className="size-4" strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>
      </div>
      <div
        className="text-text-muted mb-1.5 grid grid-cols-7 gap-x-1 text-center text-xs leading-6 font-medium"
        aria-hidden="true"
      >
        {weekdays.map((day, index) => (
          <span key={`${day}-${index}`} className="min-w-0 overflow-hidden">
            {day.slice(0, 2)}
          </span>
        ))}
      </div>
      <div ref={viewportRef} className="relative grid overflow-hidden [contain:paint]">
        {month ? (
          paneIndexes.map((index) => (
            <MonthPane key={index} index={index} position={position} present={index === target}>
              {renderMonth(fromIndex(index), index === target)}
            </MonthPane>
          ))
        ) : (
          <div className="relative col-start-1 row-start-1 min-w-0" aria-hidden="true">
            <div className="grid gap-y-1">
              {Array.from({ length: 6 }, (_, week) => (
                <div key={week} className="grid grid-cols-7 gap-x-1">
                  {Array.from({ length: 7 }, (_, day) => (
                    <span key={day} className="aspect-square" />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

/** Her ay altı hafta gösterir; ızgara tek yükseklikte kalır ve ay değişirken sıçramaz. */
function makeWeeks(month: Date, weekStartsOn: 0 | 1) {
  const start = addDays(month, -((month.getDay() - weekStartsOn + 7) % 7))
  return Array.from({ length: 6 }, (_, week) =>
    Array.from({ length: 7 }, (_, day) => addDays(start, week * 7 + day)),
  )
}

export { Calendar }
